// Pure statistics helpers for the Instrument Limits table.
//
// Extracted from src/components/InstrumentLimits.js so the pooling math can be
// unit-tested without rendering the component or mocking Firestore hooks.

import { isIncludedInSqcChartSeries } from './sqcInclusion';

// Calculate pooled values across a set of per-instrument stats.
// When `selectedOnly` is true, only instruments flagged 'Y' in
// `poolingSelections` are included.
export function calculatePooledValues(stats, selectedOnly, poolingSelections = {}) {
  if (!stats || stats.length === 0) return null;

  // Filter by selection if needed
  const filteredStats = selectedOnly
    ? stats.filter((stat) => poolingSelections[stat.instrument] === 'Y')
    : stats;

  if (filteredStats.length === 0) return null;

  // Calculate total N
  const totalN = filteredStats.reduce((sum, stat) => sum + stat.n, 0);
  if (totalN === 0) return null;

  // Calculate weighted mean
  const weightedMean =
    filteredStats.reduce((sum, stat) => {
      return sum + parseFloat(stat.rawMean) * stat.n;
    }, 0) / totalN;

  // Calculate weighted variance
  let numerator = 0;
  let denominator = 0;
  filteredStats.forEach((stat) => {
    const weight = stat.n > 1 ? stat.n - 1 : 0;
    numerator += parseFloat(stat.rawVariance) * weight;
    denominator += weight;
  });

  const weightedVariance = denominator > 0 ? numerator / denominator : 0;

  // Calculate weighted UCL and LCL
  const weightedUCL = weightedMean + 3 * Math.sqrt(weightedVariance);
  const weightedLCL = weightedMean - 3 * Math.sqrt(weightedVariance);

  return {
    n: totalN,
    mean: weightedMean.toFixed(4),
    variance: weightedVariance.toFixed(4),
    stdDev: Math.sqrt(weightedVariance).toFixed(4),
    ucl: weightedUCL.toFixed(4),
    lcl: weightedLCL.toFixed(4)
  };
}

// Compute per-instrument stats plus pooled (all) and pooled (selected) rows for
// the Instrument Limits table.
export function computeInstrumentLimitsStats({
  data,
  method,
  sampleName,
  location,
  parameter,
  summaries = [],
  poolingSelections = {},
  assignForPooling = {}
}) {
  if (!data || !method || !sampleName)
    return { instrumentStats: [], pooledValuesAll: null, pooledValuesSelected: null };

  // Group data by instrument
  const instrumentMap = {};

  // Filter data down to the current chart's live points before pooling:
  //   - must have a result value
  //   - must NOT be archived. archiveData() flags superseded rows with
  //     `archive: true` (and renames their parameter) when a chart is
  //     archived/restarted; without this filter those rows keep inflating each
  //     instrument's n so it reflects the pre-archive state instead of current.
  //   - must match the parameter being viewed, so a multi-parameter sample does
  //     not pool unrelated measurements together.
  //   - must NOT be omitted from SQC (reuses the shared, dependency-free
  //     `isIncludedInSqcChartSeries` rule). The authoritative summary-statistics
  //     n (backend fullKeyParams n_sd, via dataWithOmissionsRemoved.getMasterId
  //     which keeps only `omitFromSQC === 'N'` rows) excludes omitted points, so
  //     the Instrument Limits n must match. Silenced points (`silence === 'Y'`)
  //     are intentionally NOT excluded — the backend applies no silence filter
  //     and silence stays in the calc/series. See GitHub issue #373.
  const filteredData = data.filter((item) => {
    return (
      item.resultOmits !== undefined &&
      !item.archive &&
      isIncludedInSqcChartSeries(item) &&
      (!parameter || item.parameter === parameter)
    );
  });

  // Group filtered data by instrument
  filteredData.forEach((item) => {
    const instrumentKey = item.instrument;
    if (!instrumentMap[instrumentKey]) {
      instrumentMap[instrumentKey] = [];
    }
    instrumentMap[instrumentKey].push(Number(item.resultOmits));
  });

  // First, create base instrument stats without Z-scores
  const baseInstrumentStats = Object.keys(instrumentMap).map((instrumentKey) => {
    const values = instrumentMap[instrumentKey];

    // Calculate mean
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;

    // Calculate variance
    const variance =
      values.length <= 1
        ? 0
        : values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (values.length - 1);

    // Calculate standard deviation
    const stdDev = Math.sqrt(variance);

    const instrumentFullKey = [location, method, sampleName, instrumentKey, parameter].join('|');
    const summary = summaries.find((s) => s.id === instrumentFullKey);

    return {
      n: values.length,
      instrument: instrumentKey,
      mean: mean.toFixed(4),
      stdDev: stdDev.toFixed(4),
      variance: variance.toFixed(4),
      ucl: summary?.ucl_sd || 'N/A',
      lcl: summary?.lcl_sd || 'N/A',
      selectionForPooling: poolingSelections[instrumentKey] || 'Y',
      assignForPooling: assignForPooling[instrumentKey] || 'Y',
      rawMean: mean,
      rawVariance: variance,
      rawStdDev: stdDev
    };
  });

  // Calculate Pooled Values (all) using base stats
  const pooledValuesAll = calculatePooledValues(baseInstrumentStats, false, poolingSelections);

  // Calculate Pooled Values (selected) using base stats
  const pooledValuesSelected = calculatePooledValues(baseInstrumentStats, true, poolingSelections);

  // Now calculate Z-scores using the pooled values (selected) as reference
  const instrumentStats = baseInstrumentStats.map((stat) => {
    // Z-score calculations relative to pooled values (selected)
    let zScoreMean = 0;
    let zScoreVariance = 0;

    if (
      pooledValuesSelected &&
      pooledValuesSelected.stdDev !== 0 &&
      pooledValuesSelected.variance !== 0
    ) {
      // Z-score for mean: (instrument mean - pooled mean) / pooled stdDev
      zScoreMean =
        (stat.rawMean - parseFloat(pooledValuesSelected.mean)) /
        parseFloat(pooledValuesSelected.stdDev);

      // Z-score for variance: (instrument variance - pooled variance) / pooled variance
      // This is a simplified approach - there are more complex ways to calculate this
      zScoreVariance =
        (stat.rawVariance - parseFloat(pooledValuesSelected.variance)) /
        parseFloat(pooledValuesSelected.variance);
    }

    return {
      ...stat,
      zScoreMean: zScoreMean.toFixed(4),
      zScoreVariance: zScoreVariance.toFixed(4)
    };
  });

  return { instrumentStats, pooledValuesAll, pooledValuesSelected };
}
